import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { patchLoyaltyCardNoteThunk } from 'app/main/apps/loyalties/ducks/loyalty.thunk';
import {
  FlatLoyaltyOfferDTO,
  LoyaltyCardTransactionInDTO,
  LoyaltyOfferDTO,
  PercentageLoyaltyOfferDTO,
  ProductLoyaltyOfferDTO,
} from 'app/main/apps/loyalties/type';
import { CouponUserItemInDTO } from 'app/main/design-system/models/coupon.type';
import { ListObject, ListObjectWithStates } from 'app/main/design-system/models/listObjectsModel';
import {
  AcquiredFromTypeEnum,
  ActivityPeriod,
  ClientDetails,
  ClientEntity,
  ClientEntityList,
  ClientEntityListMetadata,
  ClientFormResponse,
  ClientRecord,
  ClientsSortKey,
  ClientsSortOrderEnum,
  ClientsSortTypeEnum,
  ClientTopProduct,
  ClientType,
  ColumnPreference,
  CrmFilter,
  FormQuestionColumn,
} from '../type';
import clientsAdapter from './clients.adapter';
import { addClientSale, deleteClientThunk, getClientsThunk, getClientTransactionsThunk } from './clients.thunk';

export const PAGE = 0;
export const ROWS_PER_PAGE: number[] = [5, 10, 25, 50];
export const CLIENTS_SORT_DEFAULT = ClientsSortTypeEnum.lastVisit;
export const CLIENTS_SORT_ORDER_DEFAULT = ClientsSortOrderEnum.descending;

export interface ClientState {
  ids: string[];
  entities: { [key: string]: ClientEntity };
  metadata: ClientEntityListMetadata;
  hiddenPartialClients: number;
  sort: {
    type?: ClientsSortKey;
    order?: ClientsSortOrderEnum;
  };
  filters: {
    searchText: string;
    acquiredFrom: AcquiredFromTypeEnum[];
    clientsType: ClientType[];
    segmentRefs: string[];
    crmFilter?: CrmFilter;
    selectedStore?: string;
    record?: ClientRecord;
    activityPeriod?: ActivityPeriod;
  };
  pagination: {
    page: number;
    rowsPerPage: number;
  };
  isLoading: boolean;
  clientListState: {
    isClientListLoading: boolean;
    requestId: string | null;
  };
  isClientLoyaltyLoading: boolean;
  columnPreferences: ColumnPreference[];
  columnsDialogOpened: boolean;
  addColumnDrawerOpened: boolean;
  isClientActiveCouponsLoading: boolean;
  hasLoadedFiltersSortPagination: boolean;
  hasError: boolean;
  notFoundClientId: string | null;
  selectedClient: {
    info: ClientDetails | null;
    loyaltyOffers: (FlatLoyaltyOfferDTO | PercentageLoyaltyOfferDTO | ProductLoyaltyOfferDTO | LoyaltyOfferDTO)[];
    activeCoupons: CouponUserItemInDTO[];
  };
  currentClientCoupons: ListObjectWithStates<CouponUserItemInDTO>;
  currentClientTransactions: ListObjectWithStates<LoyaltyCardTransactionInDTO>;
  currentClientTransactionsRequestId: string | null;
  currentClientTopProducts: {
    data: ClientTopProduct[];
    isLoading: boolean;
    hasError: boolean;
  };
  formQuestionColumns: {
    data: FormQuestionColumn[];
    isLoading: boolean;
    hasError: boolean;
  };
  currentClientFormResponses: {
    data: ClientFormResponse[];
    clientId: string | null;
    total: number;
    isLoading: boolean;
    hasError: boolean;
  };
  creditPointsModal: {
    isOpen: boolean;
    isLoading: boolean;
  };
  userDialogOpened: boolean;
  editUserDialogOpened: boolean;
  redeemOfferDialog: {
    isOpen: boolean;
    isLoading: boolean;
    isOffersLoading?: boolean;
  };
  redeemCouponDialog: {
    isOpen: boolean;
    isLoading: boolean;
  };
}

const initialState: ClientState = {
  ids: [],
  entities: {},
  metadata: {
    total: 0,
  },
  hiddenPartialClients: 0,
  sort: {
    type: CLIENTS_SORT_DEFAULT,
    order: CLIENTS_SORT_ORDER_DEFAULT,
  },
  filters: {
    searchText: '',
    acquiredFrom: [],
    clientsType: [],
    segmentRefs: [],
    crmFilter: undefined,
    selectedStore: '',
    record: undefined,
    activityPeriod: undefined,
  },
  pagination: {
    page: PAGE,
    rowsPerPage: ROWS_PER_PAGE[0],
  },
  hasLoadedFiltersSortPagination: false,
  isLoading: false,
  clientListState: {
    isClientListLoading: false,
    requestId: null,
  },
  isClientLoyaltyLoading: false,
  columnPreferences: [],
  columnsDialogOpened: false,
  addColumnDrawerOpened: false,
  isClientActiveCouponsLoading: false,
  hasError: false,
  notFoundClientId: null,
  selectedClient: {
    info: null,
    activeCoupons: [],
    loyaltyOffers: [],
  },
  currentClientCoupons: {
    data: [],
    metadata: {
      page: null,
      size: null,
      total: 0,
    },
    isLoading: false,
    hasError: false,
  },
  currentClientTransactions: {
    data: [],
    metadata: {
      page: null,
      size: null,
      total: 0,
    },
    isLoading: false,
    hasError: false,
  },
  currentClientTransactionsRequestId: null,
  currentClientTopProducts: {
    data: [],
    isLoading: false,
    hasError: false,
  },
  formQuestionColumns: {
    data: [],
    isLoading: false,
    hasError: false,
  },
  currentClientFormResponses: {
    data: [],
    clientId: null,
    total: 0,
    isLoading: false,
    hasError: false,
  },
  creditPointsModal: {
    isOpen: false,
    isLoading: false,
  },
  userDialogOpened: false,
  editUserDialogOpened: false,
  redeemOfferDialog: {
    isOpen: false,
    isLoading: false,
  },
  redeemCouponDialog: {
    isOpen: false,
    isLoading: false,
  },
};

export const { actions, reducer } = createSlice({
  name: 'clients',
  initialState: clientsAdapter.getInitialState(initialState),
  reducers: {
    setUserClientsSuccess: (state: ClientState, action: PayloadAction<ClientEntityList>) => {
      clientsAdapter.setAll(state, action.payload.data);
      state.metadata = action.payload.metadata;
      state.hasError = false;
    },
    setUserClientsFailed: (state: ClientState) => {
      clientsAdapter.setAll(state, []);
      state.metadata = { total: 0 };
      state.hasError = true;
    },
    setClientInformationSuccess: (state, action: PayloadAction<ClientDetails>) => {
      state.selectedClient.info = { ...action.payload }; // spread to not rewrite on selectedClient.info.note
      state.hasError = false;
      state.notFoundClientId = null;
    },
    setClientNotFound: (state, action: PayloadAction<string | null>) => {
      state.notFoundClientId = action.payload;
    },
    setClientInformationFailed: state => {
      state.selectedClient.info = null;
      state.hasError = true;
    },
    setClientLoyaltyOffersSuccess: (
      state,
      action: PayloadAction<
        (FlatLoyaltyOfferDTO | PercentageLoyaltyOfferDTO | ProductLoyaltyOfferDTO | LoyaltyOfferDTO)[]
      >,
    ) => {
      state.selectedClient.loyaltyOffers = action.payload;
    },
    setClientSelectedCouponsSuccess: (state, action: PayloadAction<CouponUserItemInDTO[]>) => {
      state.selectedClient.activeCoupons = action.payload;
    },
    toggleCreditPointsModal: (state, action: PayloadAction<boolean>) => {
      state.creditPointsModal.isOpen = action.payload;
    },
    setCreditPointsLoading: (state, action: PayloadAction<boolean>) => {
      state.creditPointsModal.isLoading = action.payload;
    },
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setClientLoyaltyLoading: (state, action) => {
      state.isClientLoyaltyLoading = action.payload;
    },
    setClientActiveCouponsLoading: (state, action) => {
      state.isClientActiveCouponsLoading = action.payload;
    },
    setUsersDialogOpened: (state, action) => {
      state.userDialogOpened = action.payload;
    },
    setEditUserDialogOpened: (state, action) => {
      state.editUserDialogOpened = action.payload;
    },
    resetClientsSlice: () => initialState,
    setClientsFilters: (state: ClientState, action: PayloadAction<Partial<ClientState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    setColumnPreferences: (state: ClientState, action: PayloadAction<ColumnPreference[]>) => {
      state.columnPreferences = action.payload;
    },
    setColumnsDialogOpened: (state: ClientState, action: PayloadAction<boolean>) => {
      state.columnsDialogOpened = action.payload;
    },
    setAddColumnDrawerOpened: (state: ClientState, action: PayloadAction<boolean>) => {
      state.addColumnDrawerOpened = action.payload;
    },
    setClientsSort: (state: ClientState, action: PayloadAction<Partial<ClientState['sort']>>) => {
      state.sort = { ...state.sort, ...action.payload };
    },
    setClientsPagination: (state: ClientState, action: PayloadAction<Partial<ClientState['pagination']>>) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    setClientsResetFiltersSortPagination: state => {
      state.filters = initialState.filters;
      state.sort = initialState.sort;
      state.pagination = initialState.pagination;
    },
    setClientsFiltersSortPagination: (
      state,
      action: PayloadAction<{
        filters: Partial<ClientState['filters']>;
        sort: Partial<ClientState['sort']>;
        pagination: Partial<ClientState['pagination']>;
      }>,
    ) => {
      state.filters = { ...state.filters, ...action.payload.filters };
      state.sort = { ...state.sort, ...action.payload.sort };
      state.pagination = { ...state.pagination, ...action.payload.pagination };
      state.hasLoadedFiltersSortPagination = true;
    },
    setClientCouponsSuccess: (state, action: PayloadAction<ListObject<CouponUserItemInDTO>>) => {
      state.currentClientCoupons = {
        data: action.payload.data,
        metadata: action.payload.metadata,
        isLoading: false,
        hasError: false,
      };
    },
    setClientCouponsFailed: state => {
      state.currentClientCoupons = {
        data: [],
        metadata: {
          page: null,
          size: null,
          total: 0,
        },
        isLoading: false,
        hasError: true,
      };
    },
    setClientCouponsLoading: (state, action: PayloadAction<boolean>) => {
      state.currentClientCoupons.isLoading = action.payload;
    },
    setClientTopProductsSuccess: (state, action: PayloadAction<ClientTopProduct[]>) => {
      state.currentClientTopProducts = {
        data: action.payload,
        isLoading: false,
        hasError: false,
      };
    },
    setClientTopProductsFailed: state => {
      state.currentClientTopProducts = {
        data: [],
        isLoading: false,
        hasError: true,
      };
    },
    setClientTopProductsLoading: (state, action: PayloadAction<boolean>) => {
      state.currentClientTopProducts.isLoading = action.payload;
    },
    setFormQuestionColumnsSuccess: (state, action: PayloadAction<FormQuestionColumn[]>) => {
      state.formQuestionColumns = {
        data: action.payload,
        isLoading: false,
        hasError: false,
      };
    },
    setFormQuestionColumnsFailed: state => {
      state.formQuestionColumns = {
        data: [],
        isLoading: false,
        hasError: true,
      };
    },
    setFormQuestionColumnsLoading: (state, action: PayloadAction<boolean>) => {
      state.formQuestionColumns.isLoading = action.payload;
    },
    setClientFormResponsesSuccess: (
      state,
      action: PayloadAction<{ data: ClientFormResponse[]; total: number; clientId: string }>,
    ) => {
      state.currentClientFormResponses = {
        data: action.payload.data,
        clientId: action.payload.clientId,
        total: action.payload.total,
        isLoading: false,
        hasError: false,
      };
    },
    setClientFormResponsesFailed: state => {
      state.currentClientFormResponses = {
        data: [],
        clientId: null,
        total: 0,
        isLoading: false,
        hasError: true,
      };
    },
    setClientFormResponsesLoading: (state, action: PayloadAction<boolean>) => {
      state.currentClientFormResponses.isLoading = action.payload;
    },
    setRedeemOfferDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.redeemOfferDialog.isOpen = action.payload;
    },
    setRedeemOfferDialogLoading: (state, action: PayloadAction<boolean>) => {
      state.redeemOfferDialog.isLoading = action.payload;
    },
    setRedeemCouponDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.redeemCouponDialog.isOpen = action.payload;
    },
    setRedeemCouponDialogLoading: (state, action: PayloadAction<boolean>) => {
      state.redeemCouponDialog.isLoading = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(getClientsThunk.pending, (state, action) => {
      state.clientListState.isClientListLoading = true;
      state.clientListState.requestId = action.meta.requestId;
    });
    builder.addCase(getClientsThunk.fulfilled, (state, action) => {
      if (action.meta.requestId !== state.clientListState.requestId) {
        return;
      }
      state.clientListState.isClientListLoading = false;
      state.clientListState.requestId = null;
      clientsAdapter.setAll(state, action.payload.data);
      state.metadata = action.payload.metadata;
      state.hiddenPartialClients = action.payload.hiddenPartialClients ?? 0;
      state.hasError = false;
    });
    builder.addCase(getClientsThunk.rejected, (state, action) => {
      if (action.meta.requestId !== state.clientListState.requestId) {
        return;
      }
      state.clientListState.isClientListLoading = false;
      state.clientListState.requestId = null;
      clientsAdapter.setAll(state, []);
      state.metadata = { total: 0 };
      state.hiddenPartialClients = 0;
      state.hasError = true;
    });
    builder.addCase(getClientTransactionsThunk.pending, (state, action) => {
      state.currentClientTransactions.isLoading = true;
      state.currentClientTransactions.hasError = false;
      state.currentClientTransactionsRequestId = action.meta.requestId;
    });
    builder.addCase(getClientTransactionsThunk.fulfilled, (state, action) => {
      if (action.meta.requestId !== state.currentClientTransactionsRequestId) {
        return;
      }
      state.currentClientTransactions = {
        data: action.payload.data,
        metadata: action.payload.metadata,
        isLoading: false,
        hasError: false,
      };
      state.currentClientTransactionsRequestId = null;
    });
    builder.addCase(getClientTransactionsThunk.rejected, state => {
      state.currentClientTransactions = {
        data: [],
        metadata: {
          page: null,
          size: null,
          total: 0,
        },
        isLoading: false,
        hasError: true,
      };
      state.currentClientTransactionsRequestId = null;
    });
    builder.addCase(deleteClientThunk.pending, state => {
      state.isLoading = true;
    });
    builder.addCase(deleteClientThunk.fulfilled, state => {
      state.isLoading = false;
    });
    builder.addCase(deleteClientThunk.rejected, state => {
      state.isLoading = false;
    });
    builder.addCase(addClientSale.pending, state => {
      state.isLoading = true;
    });
    builder.addCase(addClientSale.fulfilled, state => {
      state.isLoading = false;
    });
    builder.addCase(addClientSale.rejected, state => {
      state.isLoading = false;
    });
    builder.addCase(patchLoyaltyCardNoteThunk.pending, state => {
      state.isLoading = true;
    });
    builder.addCase(patchLoyaltyCardNoteThunk.fulfilled, (state, action) => {
      if (state.selectedClient.info) {
        state.selectedClient.info.note = action.payload.note;
      }
      state.isLoading = false;
    });
    builder.addCase(patchLoyaltyCardNoteThunk.rejected, state => {
      state.isLoading = false;
    });
  },
});

export const {
  setUserClientsSuccess,
  setUserClientsFailed,
  setClientInformationSuccess,
  setClientInformationFailed,
  setClientNotFound,
  setCreditPointsLoading,
  toggleCreditPointsModal,
  setUsersDialogOpened,
  setEditUserDialogOpened,
  setLoading,
  setClientLoyaltyLoading,
  setClientActiveCouponsLoading,
  resetClientsSlice,
  setClientsFilters,
  setClientsSort,
  setColumnPreferences,
  setColumnsDialogOpened,
  setAddColumnDrawerOpened,
  setClientsPagination,
  setClientsResetFiltersSortPagination,
  setClientsFiltersSortPagination,
  setClientCouponsSuccess,
  setClientSelectedCouponsSuccess,
  setClientCouponsFailed,
  setClientCouponsLoading,
  setClientTopProductsSuccess,
  setClientTopProductsFailed,
  setClientTopProductsLoading,
  setFormQuestionColumnsSuccess,
  setFormQuestionColumnsFailed,
  setFormQuestionColumnsLoading,
  setClientFormResponsesSuccess,
  setClientFormResponsesFailed,
  setClientFormResponsesLoading,
  setRedeemOfferDialogOpen,
  setRedeemOfferDialogLoading,
  setRedeemCouponDialogOpen,
  setRedeemCouponDialogLoading,
} = actions;

export default reducer;
